import { classnames } from 'helpers/utils';
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { AppContext } from 'context';
import {
  OnboardingController,
  OnboardingSnapshot,
} from 'networking/controllers/onboarding-controller';
import { goToPage, RouteName } from 'routes';
import { history } from 'routes/routes';
import {
  captureIntroduction,
  DashboardHandoff,
  HandoffSnapshot,
} from './dashboard-handoff';
import { IntroFrame } from './intro-frame';
import { useAdvanceOnboarding } from './onboarding-status';
import { OnboardingScenes } from './scenes/onboarding-scenes';
import { arrangeMedia } from './onboarding-media';
import { SCENE_COUNT, SceneMedia } from './scenes/scene-markup';
import { onboardingCopy } from './onboarding-copy';
import { WelcomeCards } from './welcome-cards';
import { WelcomeMemoryMotion } from './welcome-memory-motion';
import './intro-frame.css';
import './onboarding-glow.css';

const PAGE_COUNT = 1 + SCENE_COUNT;

const HANDOFF_ATTRIBUTE = 'data-onboarding-handoff';

type Phase = 'idle' | 'loading' | 'intro' | 'handoff' | 'done';

// The introduction opens over the vault pages only (where sign-in and "/"
// land), never over sign-up, email verification or shared links.
const isVaultsPath = (path: string) =>
  path === '/vaults' || (path.startsWith('/vaults/') && path !== '/vaults/new');

// The introduction every account sees once (users.onboarding_status =
// pending), in one of two variants: returning for existing customers, new for
// accounts created after the new site launched (only the copy differs). It
// introduces the vault with the most memories they can see, using up to four
// of its pictures, and ends on that vault's dashboard with New glowing. A new
// owner without a vault waits: it opens once they have one. Viewers get the
// introduction only (no New step), and people with only an album or memory
// shared end on what was shared.
export const ReturningOnboarding = () => {
  const { state } = useContext(AppContext);
  const { user } = state.data;
  const advance = useAdvanceOnboarding();
  const [path, setPath] = useState(history.location.pathname);
  const [phase, setPhase] = useState<Phase>('idle');
  const [snapshot, setSnapshot] = useState<OnboardingSnapshot | null>(null);
  const [page, setPage] = useState(0);
  const overlayRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const coverRef = useRef<HTMLDivElement>(null);
  const flightsRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const handoffSnapshot = useRef<HandoffSnapshot | null>(null);
  // The path the API was last asked about, so a new owner without a vault is
  // asked again on the next page, not on every render.
  const askedPath = useRef<string | null>(null);

  useEffect(() => history.listen((location) => setPath(location.pathname)), []);

  const eligible =
    user.id > 0 &&
    user.verified &&
    user.onboardingStatus === 'pending' &&
    user.permissions?.id !== 'temporary' &&
    isVaultsPath(path);

  useEffect(() => {
    if (!eligible || phase !== 'idle' || askedPath.current === path) return;
    askedPath.current = path;
    setPhase('loading');
    OnboardingController.get()
      .then((result) => {
        if (!result.vault) {
          // Returning: nothing to introduce, and the API has already completed
          // it. New: it waits until the account has a vault.
          if (result.variant === 'returning') advance('completed');
          setPhase(result.variant === 'returning' ? 'done' : 'idle');
          return;
        }
        setSnapshot(result);
        setPage(0);
        setPhase('intro');
        // The dashboard (or what was shared) loads underneath while the
        // introduction plays, so the handoff can land on its real controls.
        const { landingPath, id } = result.vault;
        if (landingPath) history.push(landingPath);
        else goToPage(RouteName.VaultDetail, { id });
      })
      .catch(() => setPhase('done'));
  }, [eligible, phase, path]);

  const showing = phase === 'intro' || phase === 'handoff';

  // The page underneath doesn't scroll while the overlay is up.
  useEffect(() => {
    if (!showing) return undefined;
    const { overflow } = document.body.style;
    const { scrollbarGutter } = document.documentElement.style;
    // Hiding the scrollbar would widen the page and shift the controls the
    // handoff flies to (New) once it comes back; the gutter keeps them still.
    document.body.style.overflow = 'hidden';
    document.documentElement.style.scrollbarGutter = 'stable';
    return () => {
      document.body.style.overflow = overflow;
      document.documentElement.style.scrollbarGutter = scrollbarGutter;
    };
  }, [showing]);

  useEffect(() => {
    if (phase === 'intro') overlayRef.current?.focus({ preventScroll: true });
  }, [phase]);

  const media = useMemo<SceneMedia[]>(
    () =>
      (snapshot?.images ?? []).map((image) => ({
        id: String(image.id),
        kind: image.kind,
        title: image.name,
        image: image.imageUrl,
        duration: image.duration,
      })),
    [snapshot],
  );
  const arranged = useMemo(() => arrangeMedia(media), [media]);
  const recipient = useMemo(
    () => ({
      firstName: user.name.trim().split(/\s+/)[0] ?? '',
      avatarUrl: null,
    }),
    [user.name],
  );

  useLayoutEffect(() => {
    if (phase !== 'handoff') return undefined;
    const overlay = overlayRef.current;
    const cover = coverRef.current;
    const flights = flightsRef.current;
    const captured = handoffSnapshot.current;
    if (!overlay || !cover || !flights || !captured) {
      setPhase('done');
      return undefined;
    }
    const albums = new Map(
      (snapshot?.images ?? []).map((image) => [
        String(image.id),
        image.albumId,
      ]),
    );
    const handoff = new DashboardHandoff({
      overlay,
      cover,
      flights,
      status: statusRef.current,
      snapshot: captured,
      albumOf: (memoryId) => albums.get(memoryId),
      onDone: () => {
        document.documentElement.removeAttribute(HANDOFF_ATTRIBUTE);
        handoffSnapshot.current = null;
        setPhase('done');
      },
    });
    handoff.start();
    return () => handoff.cancel();
  }, [phase]);

  useEffect(
    () => () => document.documentElement.removeAttribute(HANDOFF_ATTRIBUTE),
    [],
  );

  if (!showing || !snapshot) return null;

  // The New step only exists for people who can add memories; for the rest
  // the introduction is the whole onboarding.
  const withNewStep = snapshot.canAddMemories;
  // The pictures only travel into a vault dashboard, not onto a shared album.
  const withHandoff = withNewStep && !snapshot.vault?.landingPath;

  // "Open my Vault": straight to the dashboard, New still glowing.
  const exit = () => {
    advance(withNewStep ? 'intro_seen' : 'completed');
    setPhase('done');
  };

  // "Continue to Memories": the button and pictures travel into the dashboard.
  const complete = () => {
    if (!withHandoff) {
      exit();
      return;
    }
    if (frameRef.current)
      handoffSnapshot.current = captureIntroduction(frameRef.current);
    document.documentElement.setAttribute(HANDOFF_ATTRIBUTE, '');
    advance('intro_seen');
    setPhase('handoff');
  };

  const copy = onboardingCopy(snapshot.variant, snapshot.canAddMemories);
  const { firstName } = recipient;
  const [providerLine, contentsLine] = copy.subtitle(
    snapshot.vault?.providerName ?? null,
    (snapshot.vault?.memoriesCount ?? 0) > 0,
  );
  const titles = [
    copy.title(firstName),
    ...copy.scenes.map((scene) => scene.title),
  ];
  const subtitles = [
    <>
      {providerLine}
      <br />
      {contentsLine}
    </>,
    ...copy.scenes.map((scene) => scene.subtitle),
  ];
  const nextLabel = (current: number) => {
    if (current === 0) return copy.firstAction;
    if (current === PAGE_COUNT - 1) return copy.lastAction;
    return 'Next';
  };

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className={classnames('gs-returning-onboarding', {
        'is-handoff': phase === 'handoff',
      })}
      role="dialog"
      aria-modal="true"
      aria-label={copy.dialogLabel}
      tabIndex={-1}
    >
      {phase === 'intro' ? (
        <div ref={frameRef} className="gs-returning-onboarding-frame">
          <IntroFrame
            page={page}
            totalSteps={PAGE_COUNT}
            title={titles[page]}
            subtitle={subtitles[page]}
            nextLabel={nextLabel(page)}
            onBack={page === 0 ? null : () => setPage(page - 1)}
            onNext={
              page === PAGE_COUNT - 1 ? complete : () => setPage(page + 1)
            }
            onExit={exit}
          >
            <WelcomeMemoryMotion page={page}>
              {page === 0 ? (
                <WelcomeCards media={arranged} />
              ) : (
                <OnboardingScenes
                  step={page - 1}
                  media={arranged}
                  recipient={recipient}
                  scenes={copy.scenes}
                  pageCount={PAGE_COUNT}
                />
              )}
            </WelcomeMemoryMotion>
          </IntroFrame>
        </div>
      ) : (
        <>
          <div ref={coverRef} className="gs-handoff-cover" />
          <div ref={flightsRef} className="gs-handoff-flights" />
          <p
            ref={statusRef}
            className="gs-handoff-status"
            role="status"
            aria-live="polite"
          />
        </>
      )}
    </div>,
    document.body,
  );
};
